import { AppConfig } from '../types/config';

/**
 * Application Configuration Service
 *
 * Loads and caches runtime configuration from settings.json
 *
 * Requirements:
 * - Runtime configuration: Load settings.json from same origin at app startup
 * - Validation: Validate all required fields exist before returning config
 * - Caching: Cache configuration in memory after first successful load
 * - Error handling: Throw descriptive errors if fetch fails or validation fails
 */

/**
 * In-memory cache for loaded configuration
 * Null indicates configuration has not been loaded yet
 */
let cachedConfig: AppConfig | null = null;

/**
 * Validates that all required configuration fields are present and non-empty
 *
 * @param config - Parsed configuration object to validate
 * @throws Error if any required field is missing or empty
 */
function validateConfig(config: any): asserts config is AppConfig {
  const requiredFields: (keyof AppConfig)[] = [
    'apiBaseUrl',
    's3BucketName',
    's3AccessRoleArn',
    'region',
    'stage',
    'version',
  ];

  const missingFields = requiredFields.filter(
    (field) => !config[field] || typeof config[field] !== 'string' || config[field].trim() === '',
  );

  if (missingFields.length > 0) {
    throw new Error(
      `Invalid configuration: missing or empty required fields: ${missingFields.join(', ')}`,
    );
  }
}

/**
 * Loads configuration from settings.json
 *
 * Fetches /settings.json from the same origin, parses JSON, validates required fields,
 * and caches the result in memory.
 *
 * @returns Promise resolving to validated AppConfig object
 * @throws Error if fetch fails, JSON parsing fails, or validation fails
 *
 * Requirements:
 * - Fetch settings.json from same origin (served by CloudFront)
 * - Parse JSON response
 * - Validate all required fields exist
 * - Cache configuration in memory
 * - Provide descriptive error messages for debugging
 */
export async function loadConfig(): Promise<AppConfig> {
  try {
    // Fetch settings.json from same origin
    const response = await fetch('/settings.json', {
      cache: 'no-cache', // Always fetch fresh config (important for deployments)
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch configuration: ${response.status} ${response.statusText}`);
    }

    // Parse JSON response
    const config = await response.json();

    // Validate required fields
    validateConfig(config);

    // Cache configuration
    cachedConfig = config;

    console.log(`Configuration loaded successfully for stage: ${config.stage}`);
    return config;
  } catch (error) {
    // Provide detailed error message for debugging
    if (error instanceof Error) {
      throw new Error(`Configuration loading failed: ${error.message}`);
    }
    throw new Error('Configuration loading failed: Unknown error');
  }
}

/**
 * Returns cached configuration
 *
 * @returns Cached AppConfig object
 * @throws Error if configuration has not been loaded yet
 *
 * Usage:
 * - Call loadConfig() first during app initialization
 * - Then use getConfig() throughout the application to access configuration
 */
export function getConfig(): AppConfig {
  if (!cachedConfig) {
    throw new Error('Configuration not loaded. Call loadConfig() before accessing configuration.');
  }
  return cachedConfig;
}

/**
 * Clears cached configuration
 *
 * Useful for testing or forcing a configuration reload
 */
export function clearConfigCache(): void {
  cachedConfig = null;
}
